import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import PieChart from './PieChart';
import './Dashboard.css';

function Dashboard() {
  const [units, setUnits] = useState([]);
  const [spaceData, setSpaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const loadUnits = async () => {
    setLoading(true);
    setError(null);

    try {
      const [unitsData, spaceResponse] = await Promise.all([
        apiService.fetchUnits(),
        apiService.fetchSpace()
      ]);

      setUnits(unitsData.units || []);

      // Sum up all database sizes
      if (Array.isArray(spaceResponse)) {
        const totalMB = spaceResponse.reduce((sum, db) => {
          return sum + (parseFloat(db.size_mb) || 0);
        }, 0);
        setSpaceData({ totalMB, databases: spaceResponse });
      }
    } catch (err) {
      setError(`Error loading data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteUnit = async (unit, event) => {
    // The unit card wraps the button in a <Link>; stop the click so we don't
    // navigate to the unit detail page while asking for confirmation.
    event.preventDefault();
    event.stopPropagation();

    const confirmed = window.confirm(
      `Delete unit "${unit}"? This drops all its data and per-sensor tables. This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await apiService.deleteUnit(unit);
      await loadUnits();
    } catch (err) {
      setError(`Failed to delete unit "${unit}": ${err.message}`);
    }
  };

  const isAdmin = user?.username === 'admin';

  useEffect(() => {
    loadUnits();
  }, []);

  return (
    <div className="dashboard-container">
      <div className="container">
        <div className="header-bar">
          <h1>📊 Sensor Units Dashboard</h1>
          <div className="user-info">
            <span className="username">👤 {user?.username}</span>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
        
        {!loading && (
          <div className="stats-grid">
            {units.length > 0 && (
              <div className="stats">
                <h2>Total Units</h2>
                <div className="count">{units.length}</div>
              </div>
            )}

            {spaceData && (
              <div className="stats space-stats">
                <h2>💾 Database Storage</h2>
                <PieChart usedMB={spaceData.totalMB} maxMB={100} />
                <div className="database-details">
                  {spaceData.databases.map((db, index) => (
                    <div key={index} className="db-item">
                      <span className="db-name">{db.database_name}</span>
                      <span className="db-size">{db.size_mb} MB</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {loading && <div className="loading">Loading units...</div>}
        
        {error && <div className="error">{error}</div>}
        
        <div className="units-grid">
          {!loading && units.length === 0 && !error && (
            <div className="empty-state">
              <h2>No Units Found</h2>
              <p>There are no sensor units in the database yet.</p>
            </div>
          )}
          
          {units.map(unit => (
            <Link
              key={unit}
              to={`/unit/${encodeURIComponent(unit)}`}
              className="unit-card"
            >
              <h3>📡 {unit}</h3>
              <div className="unit-id">Click to view details</div>
              {isAdmin && (
                <button
                  type="button"
                  className="unit-delete-btn"
                  onClick={(e) => handleDeleteUnit(unit, e)}
                  aria-label={`Delete unit ${unit}`}
                >
                  🗑 Delete
                </button>
              )}
            </Link>
          ))}
        </div>
        
        {!loading && (
          <button className="refresh-btn" onClick={loadUnits}>
            🔄 Refresh
          </button>
        )}
      </div>
    </div>
  );
}

export default Dashboard;

